<template>
  <v-row>
    <v-col cols="12">
      <v-breadcrumbs :items="crumbs" class="pa-0 mb-2"></v-breadcrumbs>

      <v-card :loading="loading">
        <v-card-title class="py-3">
          <v-btn icon :to="'/report/budget-monitoring'" class="mr-2"
            ><v-icon>mdi-arrow-left</v-icon></v-btn
          >
          <div>
            <div class="headline">
              {{ summary.poNumber || "PO #" + crmPoId }}
              <span v-if="summary.title" class="grey--text text--darken-1"
                >— {{ summary.title }}</span
              >
            </div>
            <div class="caption grey--text text--darken-1">
              {{ summary.customer || "—" }} · Sales {{ summary.sales || "—" }} · PO
              {{ formatDate(summary.poDate) }} · {{ summary.currency || "IDR" }}
              <span v-if="detail && detail.jobs && detail.jobs.length">
                · Job:
                <router-link
                  v-for="j in detail.jobs"
                  :key="j.jobId"
                  :to="'/report/budget-monitoring/job/' + j.jobId"
                  class="mr-1"
                  >{{ j.jobName }}</router-link
                >
              </span>
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-chip large dark :color="bandColor(summary.band)" class="mr-3">
            {{ bandLabel(summary.band) }} · {{ formatPct(summary.ratioPercent) }}
          </v-chip>
          <v-btn outlined color="primary" class="mr-2" :loading="recomputing" @click="recompute">
            <v-icon left>mdi-refresh</v-icon>Hitung ulang
          </v-btn>
          <v-btn color="success" :loading="exporting" @click="exportExcel">
            <v-icon left>mdi-file-excel</v-icon>Ekspor Excel
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="6" md="2">
              <v-card outlined class="pa-3 fill-height">
                <div class="caption grey--text text-uppercase">Subtotal PO</div>
                <div class="title">{{ summary.currency }} {{ formatIdr(summary.subtotal) }}</div>
                <div
                  class="caption grey--text"
                  v-if="summary.currency && summary.currency !== 'IDR'"
                >
                  = Rp {{ formatIdr(summary.subtotalIdr) }} (kurs
                  {{ formatIdr(detail && detail.rateToIdr) }})
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-card outlined class="pa-3 fill-height">
                <div class="caption grey--text text-uppercase">Margin</div>
                <div class="title">{{ formatPct(summary.marginPercent) }}</div>
                <div class="caption grey--text">{{ marginSourceLabel(summary.marginSource) }}</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card outlined class="pa-3 fill-height">
                <div class="caption grey--text text-uppercase">Budget</div>
                <div class="title">Rp {{ formatIdr(summary.budgetIdr) }}</div>
                <div class="caption grey--text">subtotal × (1 − margin)</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card outlined class="pa-3 fill-height">
                <div class="caption grey--text text-uppercase">Pemakaian</div>
                <div class="title">Rp {{ formatIdr(summary.usageIdr) }}</div>
                <div class="caption">
                  Belanja Rp {{ formatIdr(summary.spendIdr) }}
                  <v-chip
                    x-small
                    color="blue lighten-5"
                    class="blue--text text--darken-2"
                    @click="openBreakdown('SPEND')"
                    >{{ summary.spendPoCount }} PO</v-chip
                  >
                  · Stok Rp {{ formatIdr(summary.stockIdr) }}
                  <v-chip
                    x-small
                    color="indigo lighten-5"
                    class="indigo--text"
                    @click="openBreakdown('STOCK')"
                    >{{ summary.stockMrCount }} MR</v-chip
                  >
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-card outlined class="pa-3 fill-height">
                <div class="caption grey--text text-uppercase">Committed · Est.</div>
                <div class="title">
                  Rp {{ formatIdr(summary.committedIdr) }}
                  <v-chip x-small class="ml-1" @click="openBreakdown('COMMITTED')">rincian</v-chip>
                </div>
                <div class="caption" :class="summary.lowConfidence ? 'error--text' : 'grey--text'">
                  <v-icon v-if="summary.lowConfidence" x-small color="error"
                    >mdi-approximately-equal</v-icon
                  >
                  estimasi {{ formatPct(summary.estimatedPercent) }} dari pemakaian
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-alert v-if="summary.fxMissing" type="warning" dense text class="mt-3 mb-0">
            Kurs tidak ditemukan untuk sebagian baris (dihitung dengan kurs 1). Lengkapi History
            Currency.
          </v-alert>

          <v-row class="mt-2">
            <v-col cols="12" md="7">
              <v-card outlined>
                <v-card-title class="subtitle-1 py-2">Per job terhubung</v-card-title>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Share budget</th>
                      <th class="text-right">Budget (IDR)</th>
                      <th class="text-right">Belanja</th>
                      <th class="text-right">Stok</th>
                      <th class="text-right">Committed</th>
                      <th class="text-right">Pemakaian</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="j in jobs" :key="j.jobId">
                      <td>
                        <router-link :to="'/report/budget-monitoring/job/' + j.jobId">{{
                          j.jobName
                        }}</router-link>
                      </td>
                      <td>
                        <span v-if="j.share !== null && j.share !== undefined">{{
                          formatPct(j.share * 100)
                        }}</span>
                        <v-chip
                          x-small
                          class="ml-1"
                          :color="
                            j.method === 'EVEN_SPLIT' ? 'orange lighten-4' : 'green lighten-4'
                          "
                          >{{ j.method || "lintas job" }}</v-chip
                        >
                      </td>
                      <td class="text-right">{{ formatIdr(j.budgetIdr) }}</td>
                      <td class="text-right">
                        {{ formatIdr(j.spendIdr) }}
                        <v-chip
                          x-small
                          color="blue lighten-5"
                          class="blue--text text--darken-2 ml-1"
                          @click="openBreakdown('SPEND', j)"
                          >{{ j.spendPoCount }} PO</v-chip
                        >
                      </td>
                      <td class="text-right">
                        {{ formatIdr(j.stockIdr) }}
                        <v-chip
                          x-small
                          color="indigo lighten-5"
                          class="indigo--text ml-1"
                          @click="openBreakdown('STOCK', j)"
                          >{{ j.stockMrCount }} MR</v-chip
                        >
                      </td>
                      <td class="text-right">{{ formatIdr(j.committedIdr) }}</td>
                      <td class="text-right font-weight-bold">{{ formatIdr(j.usageIdr) }}</td>
                    </tr>
                    <tr v-if="!jobs.length">
                      <td colspan="7" class="grey--text text-center">
                        PO ini belum terhubung ke job manapun
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card>

              <v-card outlined class="mt-4">
                <v-card-title class="subtitle-1 py-2">
                  Item PO CRM
                  <v-spacer></v-spacer>
                  <span v-if="Number(summary.unlinkedItemShare) > 0" class="caption warning--text">
                    <v-icon x-small color="warning">mdi-link-off</v-icon>
                    {{ formatPct(summary.unlinkedItemShare * 100) }} nilai item belum terhubung ke
                    job
                  </span>
                </v-card-title>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Part number</th>
                      <th>Nama</th>
                      <th class="text-right">Nilai</th>
                      <th>ID produk</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="it in items" :key="it.poProductId">
                      <td>{{ it.partNumber || "—" }}</td>
                      <td>{{ it.partName || "—" }}</td>
                      <td class="text-right">{{ formatIdr(it.totalPrice) }}</td>
                      <td class="caption grey--text">{{ it.productId || "—" }}</td>
                    </tr>
                    <tr v-if="!items.length">
                      <td colspan="4" class="grey--text text-center">Tidak ada item</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card>
            </v-col>

            <v-col cols="12" md="5">
              <v-card outlined>
                <v-card-title class="subtitle-1 py-2">
                  Riwayat rasio
                  <v-spacer></v-spacer>
                  <v-btn-toggle v-model="historyDays" dense mandatory @change="loadHistory">
                    <v-btn :value="90" x-small>90 hari</v-btn>
                    <v-btn :value="365" x-small>365 hari</v-btn>
                  </v-btn-toggle>
                </v-card-title>
                <v-card-text>
                  <svg
                    :viewBox="'0 0 ' + svg.w + ' ' + svg.h"
                    width="100%"
                    :height="svg.h"
                    v-if="history.length"
                  >
                    <rect
                      x="0"
                      :y="yFor(100)"
                      :width="svg.w"
                      :height="svg.h - yFor(100)"
                      fill="#FFF8E1"
                    ></rect>
                    <rect
                      x="0"
                      :y="yFor(80)"
                      :width="svg.w"
                      :height="yFor(100) - yFor(80)"
                      fill="#FFF8E1"
                      opacity="0.6"
                    ></rect>
                    <rect
                      x="0"
                      y="0"
                      :width="svg.w"
                      :height="yFor(100)"
                      fill="#FFEBEE"
                      opacity="0.5"
                    ></rect>
                    <rect
                      x="0"
                      :y="yFor(80)"
                      :width="svg.w"
                      :height="svg.h - yFor(80)"
                      fill="#E8F5E9"
                    ></rect>
                    <line
                      x1="0"
                      :y1="yFor(80)"
                      :x2="svg.w"
                      :y2="yFor(80)"
                      stroke="#FFA000"
                      stroke-dasharray="4 3"
                    ></line>
                    <line
                      x1="0"
                      :y1="yFor(100)"
                      :x2="svg.w"
                      :y2="yFor(100)"
                      stroke="#D32F2F"
                      stroke-dasharray="4 3"
                    ></line>
                    <polyline
                      :points="spendPoints"
                      fill="none"
                      stroke="#90A4AE"
                      stroke-width="1.5"
                    ></polyline>
                    <polyline
                      :points="usagePoints"
                      fill="none"
                      stroke="#1976D2"
                      stroke-width="2"
                    ></polyline>
                    <text
                      :x="svg.w - 4"
                      :y="yFor(lastRatio) - 4"
                      text-anchor="end"
                      font-size="11"
                      fill="#1976D2"
                    >
                      {{ formatPct(lastRatio) }}
                    </text>
                  </svg>
                  <div v-else class="grey--text caption">Belum ada riwayat.</div>
                  <div class="caption grey--text mt-1">
                    <span style="color: #1976d2">■</span> pemakaian / budget &nbsp;
                    <span style="color: #90a4ae">■</span> belanja PO / budget &nbsp; ·
                    {{ history.length }} snapshot
                  </div>
                </v-card-text>
              </v-card>

              <v-card outlined class="mt-4">
                <v-card-title class="subtitle-1 py-2">Notifikasi terakhir</v-card-title>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Waktu</th>
                      <th>Transisi</th>
                      <th>Status</th>
                      <th>Penerima</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="a in alerts" :key="a.id">
                      <td class="caption">{{ formatDateTime(a.sentAt) }}</td>
                      <td>{{ bandLabel(a.fromBand) }} → {{ bandLabel(a.toBand) }}</td>
                      <td>
                        <v-chip x-small :color="alertColor(a.status)" dark :title="a.error || ''">{{
                          a.status
                        }}</v-chip>
                      </td>
                      <td
                        class="caption text-truncate"
                        style="max-width: 180px"
                        :title="a.recipients"
                      >
                        {{ a.recipients || "—" }}
                      </td>
                    </tr>
                    <tr v-if="!alerts.length">
                      <td colspan="4" class="grey--text text-center">Belum ada notifikasi</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>

    <budget-spend-breakdown v-model="showBreakdown" :context="breakdown"></budget-spend-breakdown>
  </v-row>
</template>

<script>
import fileDownload from "js-file-download";
import BudgetSpendBreakdown from "@/components/budget/BudgetSpendBreakdown.vue";
import {
  bandColor,
  bandLabel,
  formatDate,
  formatIdr,
  formatPct,
  marginSourceLabel,
  trendPoints,
} from "@/utils/budgetMonitor";

export default {
  name: "budget-monitoring-po-detail",
  components: { BudgetSpendBreakdown },
  data: () => ({
    loading: false,
    recomputing: false,
    exporting: false,
    detail: null,
    history: [],
    historyDays: 365,
    svg: { w: 420, h: 160, max: 130 },
    showBreakdown: false,
    breakdown: { bucket: "SPEND", expectedAmount: 0 },
  }),
  computed: {
    crmPoId() {
      return Number(this.$route.params.crmPoId);
    },
    summary() {
      return (this.detail && this.detail.summary) || {};
    },
    jobs() {
      return (this.detail && this.detail.jobs) || [];
    },
    items() {
      return (this.detail && this.detail.items) || [];
    },
    alerts() {
      return (this.detail && this.detail.alerts) || [];
    },
    crumbs() {
      return [
        { text: "Report", to: "/report", exact: true },
        { text: "Budget Monitoring", to: "/report/budget-monitoring", exact: true },
        { text: this.summary.poNumber || "PO #" + this.crmPoId, disabled: true },
      ];
    },
    usagePoints() {
      return trendPoints(this.history, this.svg.w, this.svg.h, this.svg.max);
    },
    spendPoints() {
      const rows = this.history.map(h => ({
        ratioPercent:
          Number(h.budgetIdr) > 0 ? (Number(h.spendIdr) / Number(h.budgetIdr)) * 100 : null,
      }));
      return trendPoints(rows, this.svg.w, this.svg.h, this.svg.max);
    },
    lastRatio() {
      const last = this.history[this.history.length - 1];
      return last ? last.ratioPercent : null;
    },
  },
  watch: {
    "$route.params.crmPoId"() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    formatIdr,
    formatPct,
    formatDate,
    bandColor,
    bandLabel,
    marginSourceLabel,
    yFor(pct) {
      return this.svg.h - (Math.min(pct, this.svg.max) / this.svg.max) * this.svg.h;
    },
    formatDateTime(d) {
      if (!d) return "—";
      const s = typeof d === "string" ? d : new Date(d).toISOString();
      return s.substring(0, 16).replace("T", " ");
    },
    alertColor(status) {
      return status === "SENT" ? "success" : status === "FAILED" ? "error" : "grey";
    },
    async load() {
      this.loading = true;
      try {
        this.detail = await this.$store.dispatch("budgetMonitor/fetchPoDetail", this.crmPoId);
        await this.loadHistory();
      } catch (e) {
        const status = e && e.response && e.response.status;
        this.$store.dispatch("toast", {
          type: "error",
          message: status === 404 ? "PO belum pernah dihitung" : "Gagal memuat detail PO",
        });
      } finally {
        this.loading = false;
      }
    },
    async loadHistory() {
      try {
        this.history = await this.$store.dispatch("budgetMonitor/fetchPoHistory", {
          crmPoId: this.crmPoId,
          days: this.historyDays,
        });
      } catch (e) {
        this.history = [];
      }
    },
    openBreakdown(bucket, job) {
      const src = job || this.summary;
      this.breakdown = {
        crmPoId: this.crmPoId,
        jobId: job ? job.jobId : undefined,
        jobName: job ? job.jobName : undefined,
        bucket,
        expectedAmount:
          bucket === "STOCK"
            ? src.stockIdr
            : bucket === "COMMITTED"
            ? src.committedIdr
            : src.spendIdr,
        poNumber: this.summary.poNumber,
      };
      this.showBreakdown = true;
    },
    async recompute() {
      this.recomputing = true;
      try {
        const res = await this.$store.dispatch("budgetMonitor/recomputePo", this.crmPoId);
        this.$store.dispatch("toast", {
          type: res && res.status === "DONE" ? "success" : "warning",
          message: `Perhitungan ${res && res.status} (${(res && res.errors && res.errors.length) ||
            0} catatan)`,
        });
        await this.load();
      } catch (e) {
        const status = e && e.response && e.response.status;
        this.$store.dispatch("toast", {
          type: status === 409 ? "warning" : "error",
          message: status === 409 ? "Perhitungan lain sedang berjalan" : "Gagal menghitung ulang",
        });
      } finally {
        this.recomputing = false;
      }
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const res = await this.$store.dispatch("budgetMonitor/exportPo", this.crmPoId);
        fileDownload(res.data, `budget-${this.summary.poNumber || this.crmPoId}.xlsx`);
      } catch (e) {
        this.$store.dispatch("toast", { type: "error", message: "Gagal mengekspor" });
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
